import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  SalonPublicBookingApi,
  SalonPublicBookingApiError,
  type SalonPublicBookingResult,
  type SalonPublicBookingSnapshot,
} from "./public-booking-api";
import type { SalonPublicSlot } from "./public-booking-api";

function tomorrow(): string {
  const next = new Date();
  next.setDate(next.getDate() + 1);
  const year = next.getFullYear();
  const month = String(next.getMonth() + 1).padStart(2, "0");
  const day = String(next.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dayWindow(date: string): { startsAfter: string; endsBefore: string } {
  const start = Date.parse(date + "T00:00:00.000Z");
  return {
    startsAfter: new Date(start - 14 * 60 * 60 * 1000).toISOString(),
    endsBefore: new Date(start + 38 * 60 * 60 * 1000).toISOString(),
  };
}

function localDate(iso: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(iso));
  const values = new Map(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  return `${values.get("year")}-${values.get("month")}-${values.get("day")}`;
}

function localTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(iso));
}

function localDateTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    timeZone,
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

function money(amountMinor: number, currency: string): string {
  try {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amountMinor / 100);
  } catch {
    return `${currency} ${(amountMinor / 100).toLocaleString("es-AR")}`;
  }
}

function errorMessage(cause: unknown): string {
  if (cause instanceof SalonPublicBookingApiError) {
    if (cause.status === 404) {
      return "La reserva online no está disponible para este negocio.";
    }
    return cause.message;
  }
  return "No pudimos completar la operación. Intentá nuevamente.";
}

export function SalonsPublicBooking({
  slug,
  api,
}: {
  slug: string;
  api?: SalonPublicBookingApi;
}) {
  const bookingApi = useMemo(
    () => api ?? new SalonPublicBookingApi(),
    [api],
  );
  const [snapshot, setSnapshot] =
    useState<SalonPublicBookingSnapshot | null>(null);
  const [serviceId, setServiceId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [resourceId, setResourceId] = useState("");
  const [date, setDate] = useState(tomorrow);
  const [slots, setSlots] = useState<readonly SalonPublicSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState<SalonPublicBookingResult | null>(null);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const bookingKey = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    setError(null);
    bookingApi
      .snapshot(slug)
      .then((next) => {
        if (!active) return;
        setSnapshot(next);
        setServiceId(next.services[0]?.id ?? "");
      })
      .catch((cause) => {
        if (active) setError(errorMessage(cause));
      });
    return () => {
      active = false;
    };
  }, [bookingApi, slug]);

  const service = useMemo(
    () => snapshot?.services.find((item) => item.id === serviceId) ?? null,
    [snapshot, serviceId],
  );

  const locations = useMemo(() => {
    if (!snapshot || !service) return [];
    return snapshot.locations.filter(
      (location) =>
        service.locationIds.length === 0 ||
        service.locationIds.includes(location.id),
    );
  }, [snapshot, service]);

  const professionals = useMemo(() => {
    if (!snapshot || !service || !snapshot.publicBooking.allowProfessionalSelection) {
      return [];
    }
    return snapshot.professionals.filter(
      (professional) =>
        (service.eligibleResourceIds.length === 0 ||
          service.eligibleResourceIds.includes(professional.id)) &&
        (!locationId ||
          professional.locationIds.length === 0 ||
          professional.locationIds.includes(locationId)),
    );
  }, [snapshot, service, locationId]);

  function resetAvailability() {
    setSlots([]);
    setSelectedSlot(null);
    setBooking(null);
    bookingKey.current = null;
  }

  async function loadAvailability() {
    if (!snapshot || !serviceId || !date) return;
    setLoadingSlots(true);
    setError(null);
    setSelectedSlot(null);
    setBooking(null);
    bookingKey.current = null;
    try {
      const window = dayWindow(date);
      const result = await bookingApi.availability(slug, {
        serviceId,
        locationId: locationId || null,
        resourceId: resourceId || null,
        ...window,
      });
      setSlots(
        result.slots.filter(
          (slot) => localDate(slot.startsAt, snapshot.business.timezone) === date,
        ),
      );
    } catch (cause) {
      setSlots([]);
      setError(errorMessage(cause));
    } finally {
      setLoadingSlots(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!snapshot || !selectedSlot || !serviceId) return;
    setBookingBusy(true);
    setError(null);
    if (!bookingKey.current) {
      bookingKey.current = `salons-public-web-${crypto.randomUUID()}`;
    }
    try {
      const result = await bookingApi.createAppointment(
        slug,
        {
          serviceId,
          locationId: locationId || null,
          resourceId: resourceId || null,
          startsAt: selectedSlot,
          customerName: customerName.trim(),
          customerPhone: customerPhone.trim() || null,
          customerEmail: customerEmail.trim() || null,
        },
        bookingKey.current,
      );
      if (!result.ok) {
        setSlots(
          result.alternatives.filter(
            (slot) =>
              localDate(slot.startsAt, snapshot.business.timezone) === date,
          ),
        );
        setSelectedSlot(null);
        bookingKey.current = null;
        setError(
          "Ese horario acaba de ocuparse. Elegí una de las alternativas disponibles.",
        );
        return;
      }
      setBooking(result);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBookingBusy(false);
    }
  }

  if (!snapshot) {
    return (
      <main className="cd-public-booking" id="main-content">
        <section className="cd-public-booking__card cd-public-booking__state">
          <span className="cd-public-booking__brand">ClickDialog</span>
          <h1>{error ? "Reserva no disponible" : "Cargando agenda…"}</h1>
          {error && <p role="alert">{error}</p>}
        </section>
      </main>
    );
  }

  if (booking?.ok) {
    const selectedService = snapshot.services.find(
      (item) => item.id === booking.appointment.serviceId,
    );
    return (
      <main className="cd-public-booking" id="main-content">
        <section className="cd-public-booking__card cd-public-booking__success">
          <span className="cd-public-booking__brand">ClickDialog</span>
          <div className="cd-public-booking__success-mark" aria-hidden="true">
            ✓
          </div>
          <p className="cd-public-booking__eyebrow">
            {booking.deposit.required ? "Turno creado · seña pendiente" : "Reserva confirmada"}
          </p>
          <h1>Tu turno quedó registrado</h1>
          <dl className="cd-public-booking__summary">
            <div>
              <dt>Servicio</dt>
              <dd>{selectedService?.name ?? "Servicio"}</dd>
            </div>
            <div>
              <dt>Fecha</dt>
              <dd>
                {localDateTime(
                  booking.appointment.startsAt,
                  snapshot.business.timezone,
                )}
              </dd>
            </div>
            <div>
              <dt>Negocio</dt>
              <dd>{snapshot.business.publicName}</dd>
            </div>
          </dl>

          {booking.deposit.required && booking.deposit.checkout?.checkoutUrl && (
            <div className="cd-public-booking__payment">
              <strong>Falta pagar la seña</strong>
              <p>
                {booking.deposit.amountMinor != null && booking.deposit.currency
                  ? money(
                      booking.deposit.amountMinor,
                      booking.deposit.currency,
                    )
                  : "El importe se calcula con la política del negocio."}
              </p>
              <a
                className="cd-public-booking__primary"
                href={booking.deposit.checkout.checkoutUrl}
                rel="noreferrer"
              >
                Pagar seña con Mercado Pago
              </a>
              <small>
                El pago se confirma únicamente cuando Mercado Pago envía su
                webhook aprobado.
              </small>
            </div>
          )}

          {booking.deposit.required && !booking.deposit.checkout && (
            <div className="cd-public-booking__notice" role="status">
              <strong>La seña está pendiente.</strong>
              <p>
                El turno fue creado, pero el checkout de pago no está disponible
                en este momento. El negocio puede contactarte para completar el
                pago.
              </p>
            </div>
          )}

          <button
            type="button"
            className="cd-public-booking__secondary"
            onClick={() => window.location.reload()}
          >
            Hacer otra reserva
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="cd-public-booking" id="main-content">
      <section className="cd-public-booking__hero">
        <span className="cd-public-booking__brand">ClickDialog</span>
        <p className="cd-public-booking__eyebrow">Reserva online</p>
        <h1>{snapshot.business.publicName}</h1>
        {snapshot.business.publicDescription && (
          <p>{snapshot.business.publicDescription}</p>
        )}
      </section>

      <section className="cd-public-booking__card">
        <div className="cd-public-booking__step">
          <span>1</span>
          <div>
            <h2>Elegí el servicio</h2>
            <p>Los horarios salen de la agenda real del negocio.</p>
          </div>
        </div>

        {snapshot.services.length === 0 ? (
          <div className="cd-public-booking__notice">
            No hay servicios habilitados para reserva online.
          </div>
        ) : (
          <div className="cd-public-booking__form-grid">
            <label>
              <span>Servicio</span>
              <select
                value={serviceId}
                onChange={(event) => {
                  setServiceId(event.target.value);
                  setLocationId("");
                  setResourceId("");
                  resetAvailability();
                }}
              >
                {snapshot.services.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                    {item.priceMinor != null && item.currency
                      ? ` · ${money(item.priceMinor, item.currency)}`
                      : ""}
                  </option>
                ))}
              </select>
            </label>

            {locations.length > 0 && (
              <label>
                <span>Local</span>
                <select
                  value={locationId}
                  onChange={(event) => {
                    setLocationId(event.target.value);
                    setResourceId("");
                    resetAvailability();
                  }}
                >
                  <option value="">Cualquier local disponible</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {professionals.length > 0 && (
              <label>
                <span>Profesional</span>
                <select
                  value={resourceId}
                  onChange={(event) => {
                    setResourceId(event.target.value);
                    resetAvailability();
                  }}
                >
                  <option value="">Cualquier profesional</option>
                  {professionals.map((professional) => (
                    <option key={professional.id} value={professional.id}>
                      {professional.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label>
              <span>Fecha</span>
              <input
                type="date"
                value={date}
                onChange={(event) => {
                  setDate(event.target.value);
                  resetAvailability();
                }}
              />
            </label>
          </div>
        )}

        {service && (
          <div className="cd-public-booking__service-note">
            <strong>{service.name}</strong>
            <span>{service.durationMinutes} min</span>
            {service.description && <p>{service.description}</p>}
          </div>
        )}

        <button
          className="cd-public-booking__primary"
          type="button"
          disabled={!serviceId || !date || loadingSlots}
          onClick={() => void loadAvailability()}
        >
          {loadingSlots ? "Buscando horarios…" : "Ver horarios disponibles"}
        </button>

        {(slots.length > 0 || (!loadingSlots && selectedSlot === null)) && (
          <div className="cd-public-booking__slots" aria-live="polite">
            {slots.map((slot) => (
              <button
                key={slot.startsAt}
                type="button"
                className={selectedSlot === slot.startsAt ? "is-selected" : ""}
                onClick={() => {
                  setSelectedSlot(slot.startsAt);
                  setBooking(null);
                  bookingKey.current = null;
                }}
              >
                {localTime(slot.startsAt, snapshot.business.timezone)}
              </button>
            ))}
          </div>
        )}

        {!loadingSlots && slots.length === 0 && selectedSlot === null && (
          <p className="cd-public-booking__hint">
            Elegí servicio y fecha para consultar disponibilidad.
          </p>
        )}
      </section>

      {selectedSlot && (
        <form className="cd-public-booking__card" onSubmit={submit}>
          <div className="cd-public-booking__step">
            <span>2</span>
            <div>
              <h2>Completá tus datos</h2>
              <p>
                {localDateTime(selectedSlot, snapshot.business.timezone)}
              </p>
            </div>
          </div>

          <div className="cd-public-booking__form-grid">
            <label>
              <span>Nombre</span>
              <input
                required
                autoComplete="name"
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
              />
            </label>
            <label>
              <span>
                Teléfono
                {!snapshot.publicBooking.requireCustomerPhone && " · opcional"}
              </span>
              <input
                required={snapshot.publicBooking.requireCustomerPhone}
                autoComplete="tel"
                inputMode="tel"
                value={customerPhone}
                onChange={(event) => setCustomerPhone(event.target.value)}
              />
            </label>
            <label>
              <span>Email · opcional</span>
              <input
                type="email"
                autoComplete="email"
                value={customerEmail}
                onChange={(event) => setCustomerEmail(event.target.value)}
              />
            </label>
          </div>

          {snapshot.bookingRules.depositMode !== "none" && (
            <div className="cd-public-booking__notice">
              <strong>Este turno requiere seña.</strong>
              <p>
                {snapshot.bookingRules.depositMode === "percentage"
                  ? `${snapshot.bookingRules.depositValue}% del servicio`
                  : money(
                      snapshot.bookingRules.depositValue,
                      snapshot.business.defaultCurrency,
                    )}
                . Si Mercado Pago está disponible, vas a recibir el checkout
                después de registrar el turno.
              </p>
            </div>
          )}

          {error && <p className="cd-public-booking__error" role="alert">{error}</p>}

          <button
            className="cd-public-booking__primary"
            type="submit"
            disabled={
              bookingBusy ||
              !customerName.trim() ||
              (snapshot.publicBooking.requireCustomerPhone &&
                !customerPhone.trim())
            }
          >
            {bookingBusy ? "Registrando turno…" : "Confirmar reserva"}
          </button>
        </form>
      )}

      {!selectedSlot && error && (
        <p className="cd-public-booking__error" role="alert">{error}</p>
      )}

      <footer className="cd-public-booking__footer">
        Agenda y pagos operados por ClickDialog.
      </footer>
    </main>
  );
}
